import React, { useState, useEffect } from 'react'
import PropTypes from 'prop-types'
import { Download, Video, Clock, CheckCircle, AlertCircle, Loader, RefreshCw, Settings, Image } from 'lucide-react'
import { buttonVariants, componentClasses, cn } from '../../utils/designSystem'
import { getAvailableVideoTypes, downloadVideo, getCompletedVideo } from './videoUtils'
import AgentHeader from '../../components/AgentHeader'

const VideoResultPage = ({ 
  query, 
  generatedVideo, 
  isLoading, 
  error, 
  isCompleted, 
  videoType,
  aspectRatio,
  uploadedImage,
  onBack, 
  onRegenerateVideo
}) => {
  const [startTime] = useState(Date.now())
  // Removed copy functionality to hide video source
  const [showSettings, setShowSettings] = useState(false)
  const [selectedVideoType, setSelectedVideoType] = useState(videoType)
  const [showRequestIdInput, setShowRequestIdInput] = useState(false)
  const [requestId, setRequestId] = useState('')
  const [isCheckingStatus, setIsCheckingStatus] = useState(false)
  const [statusCheckError, setStatusCheckError] = useState(null)

  // Auto scroll to top when component mounts and add security measures
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' })
    
    // Disable developer tools shortcuts and screenshot attempts
    const handleKeyDown = (e) => {
      // Disable F12, Ctrl+Shift+I, Ctrl+Shift+J, Ctrl+U, PrintScreen
      if (e.key === 'F12' || 
          e.key === 'PrintScreen' ||
          (e.ctrlKey && e.shiftKey && (e.key === 'I' || e.key === 'J')) ||
          (e.ctrlKey && e.key === 'u') ||
          (e.ctrlKey && e.shiftKey && e.key === 'C')) {
        e.preventDefault()
        return false
      }
    }
    
    // Add blur effect when window loses focus (prevents screen recording)
    const handleVisibilityChange = () => {
      const videoElements = document.querySelectorAll('video')
      if (document.hidden) {
        videoElements.forEach(video => {
          video.style.filter = 'blur(10px)'
        })
      } else {
        videoElements.forEach(video => {
          video.style.filter = 'none'
        })
      }
    }
    
    document.addEventListener('keydown', handleKeyDown)
    document.addEventListener('visibilitychange', handleVisibilityChange)
    
    return () => {
      document.removeEventListener('keydown', handleKeyDown)
      document.removeEventListener('visibilitychange', handleVisibilityChange)
    }
  }, [])

  const availableVideoTypes = getAvailableVideoTypes()

  // Copy functionality removed to protect video source

  // Download video with protected URL
  const handleDownloadVideo = async () => {
    const videoUrl = generatedVideo?.url || generatedVideo?.originalUrl
    if (!videoUrl) {
      console.error('❌ No video URL available for download')
      return
    }
    
    try {
      const filename = `video-ai-${Date.now()}`
      // Download without exposing the original URL
        await downloadVideo(videoUrl, filename)
        // Download initiated successfully
    } catch (err) {
      // Error downloading video
        // Try with alternative URL if available
        if (generatedVideo?.originalUrl && videoUrl !== generatedVideo.originalUrl) {
          try {
            await downloadVideo(generatedVideo.originalUrl, filename)
            // Alternative download successful
          } catch (fallbackErr) {
            // Fallback download failed
          }
        }
    }
  }

  // Handle video type change
  const handleVideoTypeChange = (newVideoType) => {
    setSelectedVideoType(newVideoType)
    setShowSettings(false)
    onRegenerateVideo(newVideoType)
  }

  // Handle checking video status by request ID
  const handleCheckVideoStatus = async () => {
    if (!requestId.trim()) {
      setStatusCheckError('Silakan masukkan Request ID')
      return
    }

    setIsCheckingStatus(true)
    setStatusCheckError(null)

    try {
      const videoData = await getCompletedVideo(requestId.trim())
      
      // Call onRegenerateVideo with the retrieved video data
      // We need to modify this to pass the video data directly
      if (videoData && videoData.url) {
        // Create a mock regeneration that uses the existing video
        // Video found, displaying results
        
        // You might need to modify the parent component to handle this
        // For now, we'll show success message
        setStatusCheckError(null)
        setShowRequestIdInput(false)
        
        // Trigger a custom event or callback to update the video
        if (window.updateVideoFromRequestId) {
          window.updateVideoFromRequestId(videoData)
        } else {
          alert('Video ditemukan! Silakan refresh halaman untuk melihat hasil.')
        }
      }
    } catch (error) {
      setStatusCheckError('Gagal mengambil video. Pastikan Request ID benar.')
    } finally {
      setIsCheckingStatus(false)
    }
  }



  // Format generation time
  const formatGenerationTime = () => {
    if (!generatedVideo?.generatedAt) return null
    const generatedTime = new Date(generatedVideo.generatedAt)
    const duration = Math.round((generatedTime - startTime) / 1000)
    return duration > 0 ? `${duration} detik` : 'Kurang dari 1 detik'
  }

  return (
    <div className="min-h-screen bg-bg-primary overflow-x-hidden">
      {/* Header */}
      <AgentHeader
        onBack={onBack}
        icon={Video}
        title="Video Agent"
        query="Generator Video AI"
        iconColor="text-white"
        iconBgColor="bg-gradient-to-br from-red-500 to-orange-500"
        actions={isCompleted && generatedVideo ? [
          {
            onClick: () => setShowSettings(!showSettings),
            icon: Settings,
            label: 'Pengaturan',
            hideOnMobile: true,
            className: cn(buttonVariants.secondary, "flex items-center space-x-1 sm:space-x-2 px-2 sm:px-3 py-1.5 sm:py-2 text-xs sm:text-sm")
          },
          {
            onClick: handleDownloadVideo,
            icon: Download,
            label: 'Unduh',
            hideOnMobile: true,
            className: cn(buttonVariants.primary, "flex items-center space-x-1 sm:space-x-2 px-2 sm:px-3 py-1.5 sm:py-2 text-xs sm:text-sm")
          }
        ] : []}
      />

      {/* Settings Panel */}
      {showSettings && (
        <div className="max-w-6xl mx-auto px-2 sm:px-4 py-3 sm:py-4">
          <div className="bg-bg-secondary rounded-lg p-4 border border-border-secondary">
            <h3 className="text-xs sm:text-sm font-medium text-text-primary mb-2 sm:mb-3">Tipe Video</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {availableVideoTypes.map((type) => (
                <button
                  key={type.value}
                  onClick={() => handleVideoTypeChange(type.value)}
                  className={cn(
                    "p-3 rounded-lg border text-left transition-colors",
                    selectedVideoType === type.value
                      ? "border-blue-500 bg-blue-500/10 text-blue-400"
                      : "border-border-secondary bg-bg-tertiary text-text-secondary hover:border-border-primary"
                  )}
                >
                  <div className="font-medium text-sm">{type.label}</div>
                  <div className="text-xs text-text-tertiary">{type.description}</div>
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Main Content */}
      <div className="max-w-6xl mx-auto px-2 sm:px-4 py-3 sm:py-6">
        <div className="grid grid-cols-1 xl:grid-cols-3 gap-3 sm:gap-6">
          {/* Left Column - Video Display */}
          <div className="xl:col-span-2">
            <div className="bg-bg-secondary rounded-lg border border-border-secondary overflow-hidden">
              {/* Query Display */}
              <div className="p-3 sm:p-4 border-b border-border-secondary">
                <h2 className="text-base sm:text-lg font-semibold text-text-primary mb-2">Prompt</h2>
                <p className="text-sm sm:text-base text-text-secondary leading-relaxed break-words">{query}</p>
              </div>

              {/* Video Content */}
              <div className="p-3 sm:p-4">
                {isLoading && (
                  <div className="flex flex-col items-center justify-center py-12 space-y-4">
                    <div className="relative">
                      <Loader className="w-8 h-8 text-blue-500 animate-spin" />
                    </div>
                    <div className="text-center">
                      <p className="text-text-primary font-medium">Membuat video...</p>
                      <p className="text-text-tertiary text-sm mt-1">Mohon tunggu sebentar</p>
                    </div>
                  </div>
                )}

                {error && (
                  <div className="flex flex-col items-center justify-center py-12 space-y-4">
                    <AlertCircle className="w-8 h-8 text-red-500" />
                    <div className="text-center">
                      <p className="text-red-400 font-medium">Terjadi Kesalahan</p>
                      <p className="text-text-tertiary text-sm mt-1">{error}</p>
                      
                      <div className="flex flex-col sm:flex-row gap-2 mt-4">
                        <button
                          onClick={() => onRegenerateVideo(videoType)}
                          className={cn(
                            buttonVariants.primary,
                            "flex items-center space-x-2 px-4 py-2"
                          )}
                        >
                          <RefreshCw className="w-4 h-4" />
                          <span>Coba Lagi</span>
                        </button>
                        
                        {(error.includes('timeout') || error.includes('antrian') || error.includes('dashboard')) && (
                          <button
                            onClick={() => setShowRequestIdInput(!showRequestIdInput)}
                            className={cn(
                              buttonVariants.secondary,
                              "flex items-center space-x-2 px-4 py-2"
                            )}
                          >
                            <Video className="w-4 h-4" />
                            <span>Ambil Video yang Sudah Selesai</span>
                          </button>
                        )}
                      </div>
                      
                      {/* Request ID Input */}
                      {showRequestIdInput && (
                        <div className="mt-6 p-4 bg-bg-tertiary rounded-lg border border-border-secondary max-w-md mx-auto">
                          <h4 className="text-sm font-medium text-text-primary mb-3">Ambil Video dari Request ID</h4>
                          <p className="text-xs text-text-tertiary mb-3">
                            Jika video sudah selesai di dashboard fal.ai, masukkan Request ID untuk mengambil hasilnya.
                          </p>
                          
                          <div className="space-y-3">
                            <input
                              type="text"
                              value={requestId}
                              onChange={(e) => setRequestId(e.target.value)}
                              placeholder="e.g., e4af324c-755c-4f71-91b9-5779df87663e"
                              className="w-full px-3 py-2 bg-bg-primary border border-border-secondary rounded-lg text-text-primary text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            />
                            
                            {statusCheckError && (
                              <p className="text-red-400 text-xs">{statusCheckError}</p>
                            )}
                            
                            <div className="flex gap-2">
                              <button
                                onClick={handleCheckVideoStatus}
                                disabled={isCheckingStatus || !requestId.trim()}
                                className={cn(
                                  buttonVariants.primary,
                                  "flex items-center space-x-2 px-3 py-2 text-sm flex-1",
                                  (isCheckingStatus || !requestId.trim()) && "opacity-50 cursor-not-allowed"
                                )}
                              >
                                {isCheckingStatus ? (
                                  <Loader className="w-3 h-3 animate-spin" />
                                ) : (
                                  <Video className="w-3 h-3" />
                                )}
                                <span>{isCheckingStatus ? 'Mengecek...' : 'Ambil Video'}</span>
                              </button>
                              
                              <button
                                onClick={() => {
                                  setShowRequestIdInput(false)
                                  setRequestId('')
                                  setStatusCheckError(null)
                                }}
                                className={cn(
                                  buttonVariants.secondary,
                                  "px-3 py-2 text-sm"
                                )}
                              >
                                Batal
                              </button>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {isCompleted && generatedVideo && (
                  <div className="space-y-4">
                    {/* Custom Video Player */}
                    <div className="relative bg-black rounded-lg overflow-hidden shadow-lg video-container">
                      <style>{`
                        video::-webkit-media-controls-download-button {
                          display: none !important;
                        }
                        video::-webkit-media-controls-fullscreen-button {
                          display: none !important;
                        }
                        video::-internal-media-controls-download-button {
                          display: none !important;
                        }
                        .video-container {
                          -webkit-user-select: none;
                          -moz-user-select: none;
                          -ms-user-select: none;
                          user-select: none;
                          pointer-events: auto;
                        }
                        .video-container video {
                          pointer-events: auto;
                        }
                        .video-overlay {
                          position: absolute;
                          top: 0;
                          left: 0;
                          right: 0;
                          bottom: 0;
                          pointer-events: none;
                          z-index: 1;
                        }
                      `}</style>
                      <video
                        src={generatedVideo.url || generatedVideo.originalUrl}
                        controls
                        controlsList="nodownload nofullscreen noremoteplayback"
                        className="w-full h-auto"
                        style={{
                          maxHeight: '70vh',
                          objectFit: 'contain'
                        }}
                        onContextMenu={(e) => e.preventDefault()} // Disable right-click
                        onDragStart={(e) => e.preventDefault()} // Disable drag
                        onError={(e) => {
                          const currentSrc = e.target.src
                          // Error loading video - trying alternative source
                          
                          // Try fallback to alternative URL
                          if (generatedVideo.originalUrl && currentSrc !== generatedVideo.originalUrl) {
                            e.target.src = generatedVideo.originalUrl
                          } else if (generatedVideo.url && currentSrc !== generatedVideo.url) {
                            e.target.src = generatedVideo.url
                          }
                        }}
                        onLoadStart={() => {
                          // Video loading started
                        }}
                        onCanPlay={() => {
                          // Video ready to play
                        }}
                      />
                      
                      {/* Invisible overlay to prevent right-click on video */}
                      <div className="video-overlay"></div>
                      
                      {/* Action Buttons - Positioned at top right */}
                      <div className="absolute top-3 right-3 flex space-x-2 z-10">
                        {/* Copy button removed to protect video source */}
                        <button
                          onClick={handleDownloadVideo}
                          className="bg-black/60 backdrop-blur-sm text-white p-2 rounded-lg hover:bg-black/80 transition-all duration-200 opacity-80 hover:opacity-100"
                          title="Download video"
                        >
                          <Download className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                    
                    {/* Important Notice */}
                    <div className="bg-yellow-500/10 border border-yellow-500/20 rounded-lg p-4">
                      <div className="flex items-start space-x-3">
                        <AlertCircle className="w-5 h-5 text-yellow-500 flex-shrink-0 mt-0.5" />
                        <div>
                          <h4 className="text-sm font-medium text-yellow-400 mb-1">Penting: Segera Unduh Video Anda</h4>
                          <p className="text-sm text-text-secondary leading-relaxed">
                            Video ini tidak tersimpan secara permanen di server. Silakan unduh video sekarang juga untuk menyimpannya di perangkat Anda. 
                            Video mungkin tidak dapat diakses lagi setelah beberapa waktu.
                          </p>
                        </div>
                      </div>
                    </div>
                    
                    {/* Action Bar Below Video */}
                    <div className="flex items-center justify-between p-3 bg-bg-tertiary rounded-lg border border-border-secondary">
                      <div className="flex items-center space-x-3">
                        <div className="flex items-center space-x-2 text-sm text-text-secondary">
                          <Video className="w-4 h-4" />
                          <span>Video berhasil dibuat</span>
                        </div>
                        {generatedVideo.duration && (
                          <div className="flex items-center space-x-1 text-sm text-text-tertiary">
                            <Clock className="w-3 h-3" />
                            <span>{generatedVideo.duration}s</span>
                          </div>
                        )}
                      </div>
                      
                      <div className="flex items-center space-x-2">
                        <button
                          onClick={handleDownloadVideo}
                          className={cn(
                            buttonVariants.primary,
                            "flex items-center space-x-2 px-3 py-1.5 text-sm"
                          )}
                        >
                          <Download className="w-3 h-3" />
                          <span>Unduh</span>
                        </button>
                      </div>
                    </div>
                    
                    {/* Regenerate Button */}
                    <div className="flex justify-center">
                      <button
                        onClick={() => onRegenerateVideo(videoType)}
                        className={cn(
                          buttonVariants.secondary,
                          "flex items-center space-x-2 px-4 py-2"
                        )}
                      >
                        <RefreshCw className="w-4 h-4" />
                        <span>Buat Ulang Video</span>
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Right Column - Info Panel */}
          <div className="space-y-6">
            {/* Status Card */}
            <div className={cn(componentClasses.card, "p-4")}>
              <div className="flex items-center space-x-3 mb-4">
                {isLoading && <Loader className="w-5 h-5 text-blue-500 animate-spin" />}
                {error && <AlertCircle className="w-5 h-5 text-red-500" />}
                {isCompleted && <CheckCircle className="w-5 h-5 text-green-500" />}
                <h3 className="font-semibold text-text-primary">
                  {isLoading ? 'Sedang Memproses...' :
                   error ? 'Gagal' :
                   isCompleted ? 'Selesai' : 'Menunggu'}
                </h3>
              </div>
              
              {isCompleted && generatedVideo && (
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div className="flex flex-col">
                    <span className="text-text-tertiary text-xs mb-1">Waktu Generasi</span>
                    <span className="text-text-secondary font-medium">{formatGenerationTime()}</span>
                  </div>
                  <div className="flex flex-col">
                    <span className="text-text-tertiary text-xs mb-1">Tipe Video</span>
                    <span className="text-text-secondary font-medium">
                      {videoType === 'text_to_video' ? 'Text to Video' : 'Image to Video'}
                    </span>
                  </div>
                  <div className="flex flex-col">
                    <span className="text-text-tertiary text-xs mb-1">Aspect Ratio</span>
                    <span className="text-text-secondary font-medium">{aspectRatio || '16:9'}</span>
                  </div>
                  {generatedVideo.duration && (
                    <div className="flex flex-col">
                      <span className="text-text-tertiary text-xs mb-1">Durasi</span>
                      <span className="text-text-secondary font-medium">{generatedVideo.duration}s</span>
                    </div>
                  )}
                  <div className="flex flex-col">
                    <span className="text-text-tertiary text-xs mb-1">Status</span>
                    <span className="text-text-secondary font-medium">
                      <span className="text-green-400">✓ Video Siap</span>
                    </span>
                  </div>
                  {/* Seed information hidden to protect video source */}
                </div>
              )}
            </div>

            {/* Tips Card */}
            <div className={cn(componentClasses.card, "p-4")}>
              <h3 className="font-semibold text-text-primary mb-3">Tips</h3>
              <ul className="space-y-2 text-sm text-text-secondary">
                <li>• Gunakan deskripsi yang detail untuk hasil terbaik</li>
                <li>• Sebutkan gerakan, aksi, dan suasana yang diinginkan</li>
                <li>• Untuk Image to Video, upload gambar berkualitas tinggi</li>
                <li>• Gunakan kata kunci seperti "cinematic", "smooth", "dynamic"</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

VideoResultPage.propTypes = {
  query: PropTypes.string.isRequired,
  generatedVideo: PropTypes.object,
  isLoading: PropTypes.bool.isRequired,
  error: PropTypes.string,
  isCompleted: PropTypes.bool.isRequired,
  videoType: PropTypes.string.isRequired,
  aspectRatio: PropTypes.string,
  uploadedImage: PropTypes.object,
  onBack: PropTypes.func.isRequired,
  onRegenerateVideo: PropTypes.func.isRequired
}

export default VideoResultPage